<script lang="ts">
  import * as numberInput from '@zag-js/number-input';
  import { useMachine, normalizeProps } from '@zag-js/svelte';
  import { css } from '@agapi/ui/panda/css';
  import { quantitySelectorStyles } from '../quantity-selector.styles';
  import QuantitySelectorButton from './quantity-selector-button.svelte';
  import type { QuantitySelectorProps } from '../quantity-selector.types';
  import { visuallyHidden } from '@agapi/ui/panda/patterns';

  const {
    size,
    value,
    minValue,
    maxValue,
    handleIncrease,
    handleDecrease,
    onInput,
    disabled,
    css: cssProp,
    buttonStyles,
    inputStyles,
    'aria-label': ariaLabel = 'Choose amount',
    decreaseLabel = 'Decrease quantity',
    increaseLabel = 'Increase quantity',
    ...props
  }: QuantitySelectorProps = $props();

  const id = $props.id();

  const service = useMachine(numberInput.machine, {
    id,
    defaultValue: `${value}`,
    min: minValue,
    max: maxValue,
    disabled,
    clampValueOnBlur: true,
    onValueChange: (details) => {
      onInput?.(details.valueAsNumber);
    }
  });

  const api = $derived(numberInput.connect(service, normalizeProps));
  const currentValue = $derived(api.valueAsNumber);

  const classes = $derived(quantitySelectorStyles.raw({disabled}));

  function handleDecreaseClick() {
    const newValue = currentValue - 1;
    if (handleDecrease && !(minValue !== undefined && newValue < minValue)) {
      handleDecrease(newValue);
      api.setValue(newValue);
    }
  }

  function handleIncreaseClick() {
    const newValue = currentValue + 1;
    if (handleIncrease && !(maxValue !== undefined && newValue > maxValue)) {
      handleIncrease(newValue);
      api.setValue(newValue);
    }
  }
</script>

<div {...api.getRootProps()} class={css(classes.root, cssProp)}>
  <QuantitySelectorButton
    value={-1}
    disabled={disabled || (minValue !== undefined && currentValue <= minValue)}
    attributes={{
      ...api.getDecrementTriggerProps(),
      'aria-label': decreaseLabel,
      onclick: handleDecreaseClick
    }}
    size={size}
    css={{ ...classes.trigger, ...buttonStyles }}
  />
  <input
    {...api.getInputProps()}
    class={css(classes.input, {fontSize: size}, inputStyles)}
    onclick={(e) => {
      if (e.target instanceof HTMLInputElement) {
        e.target.select();
      }
    }}
    {...props}
    disabled={disabled}
  />
  <label {...api.getLabelProps()} class={visuallyHidden()}>{ariaLabel}</label>
  <QuantitySelectorButton
    value={1}
    disabled={disabled || (maxValue !== undefined && currentValue >= maxValue)}
    attributes={{
      ...api.getIncrementTriggerProps(),
      'aria-label': increaseLabel,
      onclick: handleIncreaseClick
    }}
    size={size}
    css={{ ...classes.trigger, ...buttonStyles }}
  />
</div>
